/**
 * SSI Reset Banner Component
 * 
 * This component displays an informational banner on SSI module pages when the
 * Lead clinician has triggered an SSI reset due to a conflict with their CI/DD decisions.
 * 
 * Used in the 3 Borough / SLaM workflow where:
 * 1. SSI is done first by a junior clinician
 * 2. Lead clinician does CI + DD + PAD
 * 3. Lead's findings may conflict with SSI recommendations
 * 4. Lead can reset SSI to modify symptom/domain decisions and regenerate paragraphs
 * 
 * When ssiResetRequired is true:
 * - This banner is shown to indicate the SSI module is being re-edited
 * - The Lead clinician can modify SSI decisions
 * - Once SSI is completed again, the reset flag is cleared
 * 
 * Design: Matches the Risk Banner style in RSK.js with smooth transition animation
 */

import React from 'react';
import { ExclamationIcon } from '@heroicons/react/solid';

/**
 * SSIResetBanner - Displays a notification banner when SSI reset is in progress
 * 
 * @param {Object} props
 * @param {boolean} props.isVisible - Whether the banner should be displayed
 * @param {string} props.resetRequestedByName - Name of the Lead clinician who requested the reset
 * @param {string} props.conflictType - Type of conflict ('CI' or 'DD')
 */
const SSIResetBanner = ({
  isVisible = false,
  resetRequestedByName = 'Lead clinician',
  conflictType = 'CI',
}) => {
  return (
    <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isVisible ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
      <div className="bg-amber-50 rounded-2xl p-4 text-left">
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-1">
            <ExclamationIcon className="h-5 w-5 text-amber-600" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-md font-medium text-amber-800">Changes Requested</h3>
            <div className="mt-2 text-sm text-amber-700">
              <p className="mb-2">
                <strong>{resetRequestedByName}</strong> has requested changes to the Semi-Structured Interview 
                section after identifying a conflict with their {conflictType === 'CI' ? 'Clinical Interview' : 'Diagnostic Decision'} findings.
              </p>
              <p className="font-medium mb-1">Please follow these steps:</p>
              <ol className="list-decimal list-inside space-y-1 mb-2">
                <li>Review and update the symptom and domain of functional impairment decisions to reflect the agreed clinical outcome.</li>
                <li>Complete the Semi-Structured Interview module again by finishing each section (Symptoms, Domains, and Recommended Diagnosis Decision).</li>
                <li>New AI-generated paragraphs will be created for any symptoms or domains you have changed.</li>
                <li>Go to the Review Documents page to review <strong>all</strong> Semi-Structured Interview paragraphs — including those that were not changed — and approve each one before the final report can be finalised.</li>
              </ol>
              <p className="text-xs text-amber-600 italic">
                Note: Even paragraphs with no content changes will require re-approval to confirm they still accurately reflect the updated clinical picture.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSIResetBanner;
